import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd, eventLd } from "../components/SEO";
import { ARTISTS } from "../data/artistas";
import { TICKETCORNER_URL } from "../config";
import { track, trackInitiateCheckout } from "../analytics";

export default function BilhetesPage() {
    const { t } = useLanguage();
    useSEO({
        title: t.seo.bilhetes.title,
        description: t.seo.bilhetes.desc,
        jsonLd: [
            breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.bilhetes, path: "/bilhetes" }]),
            eventLd(ARTISTS),
        ],
    });

    useEffect(() => {
        track("ViewContent", { content_name: "Bilheteira", content_type: "tickets" });
        track("begin_checkout", { content_name: "Bilheteira", currency: "CHF" });
        const holder = document.getElementById("eventim-holder");
        if (holder && !holder.querySelector("script")) {
            const s = document.createElement("script");
            s.type = "text/javascript";
            s.src = "https://www.eventim-light.com/ch/a/6a970634e6d9be8250357d67/public/iframe/el-shop.js";
            holder.appendChild(s);
        }
    }, []);

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative overflow-hidden" data-testid="bilhetes-page">
            <div className="absolute inset-0 pointer-events-none">
                <img loading="lazy" decoding="async" src="/images/ambiente.webp" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25 blur-md scale-110" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070503]/60 via-[#070503]/80 to-[#070503]" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1000px] px-5 sm:px-10 pt-44 sm:pt-36 pb-24"
            >
                <Reveal className="text-center">
                    <h1 className="font-display font-medium gold-text text-4xl sm:text-5xl tracking-[0.08em]" data-testid="bilhetes-title">
                        {t.bilhetes.titulo}
                    </h1>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <p className="font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b] mt-6">
                        {t.bilhetes.data}
                    </p>
                    <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/50 mt-2">
                        {t.bilhetes.exclusivo}
                    </p>
                    <Link
                        to="/confirmar"
                        data-testid="bilhetes-confirmar-button"
                        className="inline-block mt-8 font-caps text-sm tracking-[0.25em] uppercase bg-[#5e1224] text-[#efe6d6] border border-[#c9a24b]/50 px-10 py-4 hover:bg-[#7a1a30] hover:border-[#c9a24b] transition-colors duration-400"
                    >
                        {t.bilhetes.confirmarReserva}
                    </Link>
                </Reveal>

                <Reveal delay={0.1} className="mt-12">
                    <div className="flex flex-col items-center gap-5" data-testid="bilhetes-ticketcorner">
                        <p className="font-caps text-[10px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="bilhetes-ticketcorner-label">
                            {t.ticketcorner.label}
                        </p>
                        <a href={TICKETCORNER_URL} target="_blank" rel="noopener noreferrer" data-testid="bilhetes-ticketcorner-link" aria-label="Ticketcorner" onClick={() => trackInitiateCheckout(TICKETCORNER_URL)}>
                            <img loading="lazy" decoding="async" src="/images/ticketcorner-blue.png" alt="Ticketcorner" className="h-10 sm:h-12 w-auto" data-testid="bilhetes-ticketcorner-logo" />
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={0.12} className="mt-12">
                    <div data-testid="bilhetes-precos">
                        <p className="font-caps text-[10px] tracking-[0.35em] uppercase text-[#c9a24b] text-center" data-testid="bilhetes-precos-titulo">
                            {t.bilhetes.precosTitulo}
                        </p>
                        <div className="mt-6 grid gap-6 max-w-md mx-auto">
                            {[
                                { id: "standard", name: t.bilhetes.precosStandard, eb: "119.90", normal: "129.90" },
                                { id: "crianca", name: t.bilhetes.precosCrianca, eb: "65.00", normal: "65.00", nota: t.bilhetes.precosCriancaNota },
                            ].map((tier) => (
                                <div key={tier.id} className="border border-[#c9a24b]/20 bg-[#0a0705]/70 p-6" data-testid={`bilhetes-preco-${tier.id}`}>
                                    <h2 className="font-caps text-xs tracking-[0.3em] uppercase gold-text text-center">{tier.name}</h2>
                                    <div className="mt-5 space-y-3">
                                        <div className="flex items-baseline justify-between gap-4">
                                            <span className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/55">{t.bilhetes.precosEarlyBird}</span>
                                            <span className="font-display font-medium gold-text text-2xl">CHF {tier.eb}</span>
                                        </div>
                                        <div className="h-px bg-[#c9a24b]/15" />
                                        <div className="flex items-baseline justify-between gap-4">
                                            <span className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/55">{t.bilhetes.precosNormal}</span>
                                            <span className="font-display font-light text-xl text-[#efe6d6]/85">CHF {tier.normal}</span>
                                        </div>
                                        {tier.nota && <p className="text-xs font-light text-[#efe6d6]/50 leading-relaxed pt-1" data-testid={`bilhetes-preco-${tier.id}-nota`}>{tier.nota}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-center mt-5">
                            <Link to="/socios/vantagens" className="font-caps text-[10px] tracking-[0.22em] uppercase text-[#c9a24b] underline underline-offset-4 hover:text-[#ecd49b]" data-testid="bilhetes-socio-link">{t.bilhetes.socioLink}</Link>
                        </p>
                        <p className="text-center text-xs font-light text-[#efe6d6]/45 mt-6 max-w-xl mx-auto" data-testid="bilhetes-precos-nota">
                            {t.bilhetes.precosNota}
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.15} className="mt-10">
                    <div
                        id="eventim-holder"
                        className="border border-[#c9a24b]/30 bg-[#efe6d6] min-h-[600px]"
                        style={{ padding: 0 }}
                        data-testid="bilhetes-eventim"
                    >
                        <iframe
                            id="eventim-light-iframe-v2-6a970634e6d9be8250357d67-event-6a98447ce9e0676c9a41dba2"
                            src="https://www.eventim-light.com/ch/a/6a970634e6d9be8250357d67/iframe/e/6a98447ce9e0676c9a41dba2/"
                            width="100%"
                            data-scroll="true"
                            data-scroll-offset-y="0"
                            scrolling="no"
                            frameBorder="0"
                            title="Bilheteira oficial — Fados à Mesa"
                            name="eventim-light-iframe-v2-6a970634e6d9be8250357d67-event-6a98447ce9e0676c9a41dba2"
                        />
                    </div>
                    <p className="text-center text-xs font-light text-[#efe6d6]/40 mt-6">
                        {t.bilhetes.nota}
                        <a href="/confirmar" className="text-[#c9a24b] underline underline-offset-4 hover:text-[#ecd49b]" data-testid="bilhetes-confirmar-link">
                            fadosamesa.ch/confirmar
                        </a>
                        .
                    </p>
                </Reveal>
            </motion.main>
        </div>
    );
}
